import React, { useContext, useState } from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';

// eslint-disable-next-line import/no-unresolved
const context = React.createContext({
    validations: {},
    setValidations: () => { },
    updateValidation: () => { },
    allValid: true,
    initialValidation: 'silent',
    validation: 'noisy',
    initialState: {},
    autoDisablersWereTriggered: false,
    setAutoDisablersWereTriggered: () => { },
});
context.displayName = 'ValidationContext';
const useValidation = () => useContext(context);

const validateAll = (validation) => !Object.values(validation).some((field) => !field.valid);
const ValidationGroup = ({ children, initialValidation = 'silent', validation = 'noisy', initialState = {}, }) => {
    const [validations, setValidations] = useState(initialState);
    const [autoDisablersWereTriggered, setAutoDisablersWereTriggered] = useState(false);
    const allValid = validateAll(validations);
    const updateValidation = (key, val) => {
        setValidations(Object.assign(Object.assign({}, validations), { [key]: val }));
    };
    return (jsx(context.Provider, Object.assign({ value: {
            validations, setValidations, allValid, initialValidation, validation, updateValidation, initialState, autoDisablersWereTriggered, setAutoDisablersWereTriggered,
        } }, { children: children }), void 0));
};
ValidationGroup.displayName = 'ValidationGroup';

// eslint-disable-next-line import/no-unresolved
let Mui4Module;
let Mui5Module;
let anyModuleLoaded = false;
let debugMessage = 'mui-validate: No Material UI package found, using fallback mechanism.';
// try loading Material UI v5
try {
    // eslint-disable-next-line
    Mui5Module = require('@mui/material');
    debugMessage = 'mui-validate: Loaded @mui/material for internal usage.';
    anyModuleLoaded = true;
    // eslint-disable-next-line
}
catch (e) {
    console.info('mui-validate: ' + e.message);
}
if (!anyModuleLoaded) {
    // try loading Material UI v4
    try {
        // eslint-disable-next-line
        Mui4Module = require('@material-ui/core');
        debugMessage = 'mui-validate: Loaded @material-ui/core for internal usage.';
        anyModuleLoaded = true;
        // eslint-disable-next-line
    }
    catch (e) {
        console.info('mui-validate: ' + e.message);
    }
}
// eslint-disable-next-line
console.info(debugMessage);
const { FormControl, FormHelperText, Typography, Box, } = (Mui5Module
    || Mui4Module
    || {
        // eslint-disable-next-line
        FormControl: (props) => React.Fragment,
        // eslint-disable-next-line
        FormHelperText: (props) => React.Fragment,
        // eslint-disable-next-line
        Typography: (props) => React.Fragment,
        // eslint-disable-next-line
        Box: (props) => React.Fragment,
    });

var ERROR_MESSAGE = {
    REQUIRED: 'Please fill in this field.',
    UNIQUE: 'Please choose a unique value',
    REGEX: 'Input does not match the required pattern.',
};

const required = (value) => value !== '';
const unique = (value, compareList) => !compareList.map((val) => val.toLowerCase()).includes(value.toLowerCase());
const regex = (value, regexp) => regexp.test(value);
var validator = {
    required: {
        test: required,
        errorMessage: ERROR_MESSAGE.REQUIRED,
    },
    unique: {
        test: unique,
        errorMessage: ERROR_MESSAGE.UNIQUE,
    },
    regex: {
        test: regex,
        errorMessage: ERROR_MESSAGE.REGEX,
    },
};

const validate = (value, rules = {}) => {
    const validation = { valid: true, messages: [], display: true };
    const rulesIncluded = Object.keys(rules);
    if (rulesIncluded.includes('required') && !validator.required.test(value)) {
        validation.messages.push({
            type: 'required',
            text: (Array.isArray(rules.required) && rules.required[1]) || validator.required.errorMessage,
        });
    }
    if (rulesIncluded.includes('unique') && rules.unique && !validator.unique.test(value, 
    // eslint-disable-next-line
    // @ts-ignore:next-line
    Array.isArray(rules.unique[0]) ? rules.unique[0] : rules.unique)) {
        validation.messages.push({
            type: 'unique',
            text: (Array.isArray(rules.unique[0]) && rules.unique[1]) || validator.unique.errorMessage,
        });
    }
    if (rulesIncluded.includes('regex') && rules.regex && !validator.regex.test(value, Array.isArray(rules.regex) ? rules.regex[0] : rules.regex)) {
        validation.messages.push({
            type: 'regex',
            text: (Array.isArray(rules.regex) && rules.regex[1]) || validator.regex.errorMessage,
        });
    }
    if (rulesIncluded.includes('custom') && rules.custom && !rules.custom[0](value)) {
        validation.messages.push({
            type: 'custom',
            text: rules.custom[1],
        });
    }
    validation.valid = validation.messages.length === 0;
    return validation;
};

// eslint-disable-next-line
const detectInputType = (props) => {
    if (typeof props.autoComplete === 'boolean' || props.getOptionLabel !== undefined) {
        return 'autocomplete';
    }
    if ([
        props.allowKeyboardControl, props.KeyboardButtonProps, props.inputFormat,
        props.mask, props.minDate, props.maxDate, props.disableMaskedInput, props.disableOpenPicker,
        props.disableHighlightToday, props.desktopModeMediaQuery, props.defaultCalendarMonth,
        props.allowSameDateSelection, props.onMonthChange, props.onYearChange, props.showDaysOutsideCurrentMonth,
        props.OpenPickerButtonProps, props.renderDay, props.shouldDisableDate, props.shouldDisableYear,
        props.showTodayButton, props.todayText,
    ].some((val) => val !== undefined)) {
        return 'picker';
    }
    return 'textfield'; // select and textfield have the same behavior in the lib
};
// eslint-disable-next-line
const getValueFromAutocomplete = (option, children) => {
    var _a;
    let value;
    if (!option) {
        value = '';
    }
    else {
        // eslint-disable-next-line
        // @ts-ignore-next-line
        value = ((_a = children === null || children === void 0 ? void 0 : children.props) === null || _a === void 0 ? void 0 : _a.getOptionLabel) ? children.props.getOptionLabel(option) : option;
    }
    return value;
};

const Validate = ({ children, name, required, unique, regex, custom, after, before, initialValidation, validation, inputType = 'detect', id, }) => {
    var _a, _b;
    const { validations, updateValidation, initialValidation: initialValidationSetting, validation: validationSetting, } = useValidation();
    const initialValidationDerrived = initialValidation || initialValidationSetting;
    const validationDerrived = validation || validationSetting;
    const detectedInputType = inputType === 'detect' ? detectInputType(children.props) : inputType;
    // Validation rules which will be applied
    const validationRules = {};
    if (required) {
        validationRules.required = required;
    }
    if (unique !== undefined) {
        validationRules.unique = unique;
    }
    if (regex !== undefined) {
        validationRules.regex = regex;
    }
    if (custom !== undefined) {
        validationRules.custom = custom;
    }
    // Initial validations during first child component rendering
    // all supported child types (so far) define an initial value in the component attribut 'value'
    if (validations[name] === undefined && Object.keys(validationRules).length > 0) {
        let value;
        if (detectedInputType === 'autocomplete') {
            value = getValueFromAutocomplete(children.props.value, children);
        }
        else {
            value = children.props.value || '';
        }
        const validationResult = validate(value, validationRules);
        if (initialValidationDerrived === 'silent') {
            validationResult.display = false;
        }
        updateValidation(name, validationResult);
    }
    // eslint-disable-next-line
    const onChange = (...args) => {
        if (children.props.onChange) {
            children.props.onChange(...args);
        }
        // before hook operations
        if (before) {
            before();
        }
        // value to be found from underlying component
        let value = '';
        // autocomplete sends the attached option in the second parameter
        // in case no option is selected null is sent instead
        if (detectedInputType === 'autocomplete') {
            value = getValueFromAutocomplete(args[1], children);
            // eslint-disable-next-line
        }
        // picker send a date object or 'Invalid Date' as the first parameter
        else if (detectedInputType === 'picker') {
            if (args[0]) {
                try {
                    value = new Date(args[0]).toISOString();
                }
                catch (e) {
                    value = '';
                }
            }
            // eslint-disable-next-line
        }
        // textfield and select send a regular event as first parameter
        else if (['textfield', 'select'].includes(detectedInputType)) {
            const { value: eventValue = '' } = args[0].target;
            value = eventValue;
        }
        const validationResult = validate(value, validationRules);
        if (validationDerrived === 'silent') {
            validationResult.display = false;
        }
        updateValidation(name, validationResult);
        // after hook operations
        if (after) {
            after(validationResult);
        }
    };
    const addedProps = {
        onChange,
    };
    // lookup if error exists
    const hasError = ((_a = validations[name]) === null || _a === void 0 ? void 0 : _a.valid) === false;
    // lookup if error exists and shall be displayed
    const displayError = hasError && ((_b = validations[name]) === null || _b === void 0 ? void 0 : _b.display);
    // calculate the message to be displayed
    const message = displayError ? validations[name].messages[0].text : '';
    // This block is specifically for TextFields
    if (displayError) {
        addedProps.helperText = '';
        addedProps.error = true;
    }
    // in case there is a labelId set on the validation child, we can assume
    // that it is inside a form control, thus we cannot wrap with an own control
    // but must reuse the existing one
    const { labelId } = children.props;
    const wrapperProps = {
        error: labelId ? undefined : displayError,
        style: {
            width: children.props.fullWidth === true ? '100%' : undefined,
            display: labelId ? 'inline-block' : undefined,
        },
        'data-has-error': hasError,
        'data-has-message': message !== '',
        id,
    };
    // Form control needs to always be present so that the alignment of the
    // helper text is correct
    const Wrapper = labelId ? Box : FormControl;
    return (jsxs(Wrapper, Object.assign({}, wrapperProps, { children: [React.cloneElement(children, addedProps), jsx(FormHelperText, Object.assign({ error: displayError }, { children: message }), void 0)] }), void 0));
};
Validate.displayName = 'Validate';

// eslint-disable-next-line import/no-unresolved
const AutoDisabler = ({ children, firstDisplayErrors = false }) => {
    const { allValid, validations, setValidations, autoDisablersWereTriggered, setAutoDisablersWereTriggered, } = useValidation();
    const calculatedDisabled = !allValid // precondition is that there is at least 1 error
        && ( // additionally
        !firstDisplayErrors // firstDisplayErrors is not set to true
            || (firstDisplayErrors && ( // or it is set true
            autoDisablersWereTriggered // and a disabler button was already hit
                || Object.values(validations).some((validation) => validation.valid === false && validation.display === true) // or any error is visible
            )));
    return React.cloneElement(children, {
        onClick: !autoDisablersWereTriggered ? () => {
            // if firstDisplayErrors set display all error messages
            if (firstDisplayErrors) {
                const newValidations = Object.assign({}, validations);
                Object.keys(newValidations).forEach((key) => {
                    newValidations[key].display = true;
                });
                setValidations(newValidations);
            }
            setAutoDisablersWereTriggered(true);
            // if allValid then trigger the actual onClick event if present
            if (allValid && children.props.onClick) {
                children.props.onClick();
            }
        } : children.props.onClick,
        disabled: calculatedDisabled || children.props.disabled,
    });
};
AutoDisabler.displayName = 'AutoDisabler';

const AutoHide = ({ children, validationName }) => {
    const { validations } = useValidation();
    const display = validationName && validations[validationName] && (validations[validationName].valid || !validations[validationName].display);
    if (display) {
        return children;
    }
    return null;
};
AutoHide.displayName = 'AutoHide';

const ErrorList = ({ title, alwaysVisible = false, noErrorsText = 'No errors detected', titleVariant = 'subtitle1', errorVariant = 'caption', titleColor = 'inherit', errorColor = 'error', }) => {
    const { validations } = useValidation();
    const errors = Object.entries(validations).filter((dataset) => !dataset[1].valid && dataset[1].display);
    return (jsxs("div", Object.assign({ className: "error-list", "data-error-count": errors.length }, { children: [(errors.length > 0 || alwaysVisible) && jsx(Typography, Object.assign({ variant: titleVariant, className: "error-list__title", color: titleColor }, { children: title }), void 0), errors.map(([name, validation]) => (validation.messages.map((message) => (jsx(Typography, Object.assign({ component: "p", className: "error-list__error-message", color: errorColor, variant: errorVariant }, { children: `${name}: ${message.text}` }), name))))), alwaysVisible && errors.length === 0 && jsx(Typography, Object.assign({ component: "p", className: "error-list__no-errors-message", color: titleColor, variant: errorVariant }, { children: noErrorsText }), void 0)] }), void 0));
};
ErrorList.displayName = 'ErrorList';

export { AutoDisabler, AutoHide, ErrorList, Validate, ValidationGroup, useValidation };
